import React, { useState, useEffect } from 'react';
import { Button } from '@components';
import { Card, Form, Row, Col, ButtonGroup } from 'react-bootstrap';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Get, Put, Post, DeleteSingle } from '@app/utils/httpProvider';
import { useQuery, useQueryClient, useMutation } from 'react-query';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import {
    acceptDoc,
    acceptImage,
    messageError,
    messageToast,
    status200,
} from '../../utils/define';
import {
    ASSET_API, ASSETGROUP_API, DEPARTMENT_API, ASSETSTATUS_API,
    USAGEPURPOSE_API,
    EQUIPMENTTYPE_API,
    EQUIPMENTSOURCE_API,
    FUNDINGSOURCE_API,
    QUALITYLEVEL_API,
    CONNECTIONTYPE_API
} from '../../utils/apiUrl.js';
// Removed legacy Enum imports

import HorizontalFieldsRow from '@app/components/common/HorizontalFieldsRow';
import moment from 'moment';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import InputFile from '@app/components/input-file/InputFile';
import AccessoryTab from './components/AccessoryTab';
import MaintenanceEventTab from './components/MaintenanceEventTab';
import AccessoryHistoryTab from './components/AccessoryHistoryTab';
import InventoryTab from './components/InventoryTab';

const safeDateFormat = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (!isNaN(d.getTime())) {
        return moment(d).format('YYYY-MM-DD');
    }
    return '';
};

const schema = yup.object().shape({
    assetCode: yup.string().required(messageError.emptyMessage),
    assetName: yup.string().required(messageError.emptyMessage),
    assetGroupId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    currentDepartmentId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    assetStatusId: yup.number().required(messageError.emptyMessage).min(1, 'Vui lòng chọn hoặc nhập Id hợp lệ'),
    serialNumber: yup.string().nullable(),
    model: yup.string().nullable(),
    manufacturer: yup.string().nullable(),
    country: yup.string().nullable(),
    powerSpec: yup.string().nullable(),
    purchaseDate: yup.string().nullable(),
    purchaseValue: yup.number().nullable().min(0, 'Giá trị mua không được nhỏ hơn 0'),
});

const AssetEdit = () => {
    const history = useHistory();
    const queryClient = useQueryClient();
    const location = useLocation();
    const paramUrl = new URLSearchParams(location.search.substring(1));
    const itemId = paramUrl.get('id');

    const itemEdit = useQuery([itemId], () =>
        Get(ASSET_API.GET_ITEM, itemId)
    );

    const assetGroups = useQuery(['getAllAssetGroupsForMapping'], () =>
        Get(ASSETGROUP_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const departments = useQuery(['getAllDepartmentsForMapping'], () =>
        Get(DEPARTMENT_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const assetStatuses = useQuery(['getAllAssetStatusesForMapping'], () =>
        Get(ASSETSTATUS_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 })
    );

    const usagePurposes = useQuery(['getAllUsagePurposes'], () => Get(USAGEPURPOSE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));
    const equipmentTypes = useQuery(['getAllEquipmentTypes'], () => Get(EQUIPMENTTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));
    const equipmentSources = useQuery(['getAllEquipmentSources'], () => Get(EQUIPMENTSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));
    const fundingSources = useQuery(['getAllFundingSources'], () => Get(FUNDINGSOURCE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));
    const qualityLevels = useQuery(['getAllQualityLevels'], () => Get(QUALITYLEVEL_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));
    const connectionTypes = useQuery(['getAllConnectionTypes'], () => Get(CONNECTIONTYPE_API.GET_PAGING, null, { PageIndex: 1, PageSize: 10000 }));

    const departmentOptions = departments?.data?.data?.map(item => ({ value: item.id, label: item.departmentName })) || [];
    const assetGroupOptions = assetGroups?.data?.data?.map(item => ({ value: item.id, label: item.assetGroupName })) || [];
    const assetStatusOptions = assetStatuses?.data?.data?.map(item => ({ value: item.id, label: item.statusName })) || [];

    const usagePurposeOptions = usagePurposes?.data?.data?.map(item => ({ value: item.id, label: item.usagePurposeName })) || [];
    const equipmentTypeOptions = equipmentTypes?.data?.data?.map(item => ({ value: item.id, label: item.equipmentTypeName })) || [];
    const equipmentSourceOptions = equipmentSources?.data?.data?.map(item => ({ value: item.id, label: item.equipmentSourceName })) || [];
    const fundingSourceOptions = fundingSources?.data?.data?.map(item => ({ value: item.id, label: item.fundingSourceName })) || [];
    const qualityLevelOptions = qualityLevels?.data?.data?.map(item => ({ value: item.id, label: item.qualityLevelName })) || [];
    const connectionTypeOptions = connectionTypes?.data?.data?.map(item => ({ value: item.id, label: item.connectionTypeName })) || [];

    const editFunc = useMutation((value) =>
        Put(ASSET_API.UPDATE_ITEM, itemId, value)
    );

    const handleEdit = async (values) => {
        const model = { ...values };

        model.id = itemId;
        // Convert to numbers
        model.assetGroupId = Number(model.assetGroupId);
        model.currentDepartmentId = Number(model.currentDepartmentId);
        model.assetStatusId = Number(model.assetStatusId);
        model.purchaseValue = model.purchaseValue ? Number(model.purchaseValue) : null;

        editFunc.mutate(model, {
            onSuccess: async (res) => {
                if (res.status < 200 || res.status >= 300) {
                    let errorMessage = messageToast.messageToastUpdateError;
                    try {
                        const errData = await res.json();
                        if (errData?.message) {
                            errorMessage = errData.message;
                        } else if (errData?.errors) {
                            errorMessage = Object.values(errData.errors).flat().join(', ');
                        }
                    } catch (e) {
                        console.error("Error parsing response JSON:", e);
                    }
                    toast.error(errorMessage);
                } else {
                    toast.success(messageToast.messageToastUpdateSuccess);
                    queryClient.setQueryData(itemId, model);
                    history.goBack();
                }
            },
            onError: (err) => {
                let errorMessage = 'Đã có lỗi xảy ra, vui lòng thử lại !';
                if (err?.message) {
                    errorMessage = err.message; // toi
                } else if (err?.errors) {
                    errorMessage = Object.values(err.errors).flat().join(', ');
                }
                toast.error(errorMessage);
            }
        }); 
    };

    const isLoading = itemEdit.isLoading;
    const isError = itemEdit.isError;
    const error = itemEdit.error;

    return (
        <Card className="shadow-sm">
            <style>{`
                @media (min-width: 768px) {
                    .ghichu-label-responsive {
                        width: 12.5% !important;
                        flex: 0 0 12.5% !important;
                        max-width: 12.5% !important;
                        white-space: nowrap !important;
                    }
                    .ghichu-input-responsive {
                        width: 87.5% !important;
                        flex: 0 0 87.5% !important;
                        max-width: 87.5% !important;
                    }
                }
                @media (max-width: 767.98px) {
                    .ghichu-label-responsive {
                        width: 100% !important;
                        max-width: 100% !important;
                        text-align: left !important;
                        margin-bottom: 0.5rem !important;
                    }
                    .ghichu-input-responsive {
                        width: 100% !important;
                        max-width: 100% !important;
                        padding-left: 0px !important;
                    }
                }
            `}</style>
            <Card.Header className="bg-white d-flex align-items-center py-3">
                <h5 className="card-title fw-bold text-primary mb-0">
                    <i className="fas fa-edit mr-2" />
                    Chỉnh sửa tài sản
                </h5>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div>Đang tải...</div>
                ) : isError ? (
                    <div>Có lỗi xảy ra: ${error.message}</div>
                ) : itemEdit.data == null ? (
                    <div>Thẻ tài sản không tồn tại</div>
                ) : (
                    <Formik
                        enableReinitialize
                        validationSchema={schema}
                        onSubmit={(values) => handleEdit(values)}
                        initialValues={{
                            assetCode: itemEdit.data?.assetCode || '',
                            assetName: itemEdit.data?.assetName || '',
                            assetGroupId: itemEdit.data?.assetGroupId || 0,
                            currentDepartmentId: itemEdit.data?.currentDepartmentId || 0,
                            assetStatusId: itemEdit.data?.assetStatusId || 0,
                            serialNumber: itemEdit.data?.serialNumber || '',
                            model: itemEdit.data?.model || '',
                            manufacturer: itemEdit.data?.manufacturer || '',
                            country: itemEdit.data?.country || '',
                            powerSpec: itemEdit.data?.powerSpec || '',
                            purchaseDate: itemEdit.data?.purchaseDate ? safeDateFormat(itemEdit.data.purchaseDate) : '',
                            purchaseValue: itemEdit.data?.purchaseValue || 0,
                            isDisposed: itemEdit.data?.isDisposed || false,
                                    imagePath: itemEdit.data?.imagePath || '',
                                    imageThumbnail: itemEdit.data?.imageThumbnail || '',
                                    documentLink: itemEdit.data?.documentLink || [],
                                    usagePurposeId: itemEdit.data?.usagePurposeId || null,
                                    manager: itemEdit.data?.manager || '',
                                    equipmentTypeId: itemEdit.data?.equipmentTypeId || null,
                                    unitOfMeasurement: itemEdit.data?.unitOfMeasurement || '',
                                    equipmentSourceId: itemEdit.data?.equipmentSourceId || null,
                                    fundingSourceId: itemEdit.data?.fundingSourceId || null,
                                    qualityLevelId: itemEdit.data?.qualityLevelId || null,
                                    connectionTypeId: itemEdit.data?.connectionTypeId || null,
                                    notes: itemEdit.data?.notes || '',
                                    qrCode: itemEdit.data?.qrCode || ''
                                }}
                        >
                        {
                        (
                            {
                            handleSubmit,
                            handleChange,
                            values,
                            errors,
                            setFieldValue
                        }) => 
                            (
                            <Form noValidate onSubmit={handleSubmit}>
                                <Tabs>
                                    <TabList>
                                        <Tab>Thông tin tài sản</Tab>
                                        <Tab>Hình ảnh & Tài liệu</Tab>
                                        <Tab>Danh sách phụ kiện</Tab>
                                        <Tab>Lịch sử điều chuyển</Tab>
                                        <Tab>Lịch sử sửa chữa</Tab>
                                        <Tab>Lịch sử kiểm kê</Tab>
                                    </TabList>

                                    {/* Tab 1: Thông tin tài sản */}
                                    <TabPanel>
                                        <Row>
                                            <Col md={12}>
                                                <h6 className="mb-3 mt-3 text-primary">Thông tin chung</h6>
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Mã tài sản',
                                                            name: 'assetCode',
                                                            value: values.assetCode,
                                                            onChange: handleChange,
                                                            isInvalid: errors.assetCode,
                                                            error: errors.assetCode
                                                        },
                                                        {
                                                            label: 'Tên tài sản',
                                                            name: 'assetName',
                                                            value: values.assetName,
                                                            onChange: handleChange,
                                                            isInvalid: errors.assetName,
                                                            error: errors.assetName
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Nhóm TS',
                                                            name: 'assetGroupId',
                                                            component: (
                                                                <Select
                                                                    options={assetGroupOptions}
                                                                    value={assetGroupOptions.find(opt => opt.value === values.assetGroupId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('assetGroupId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn nhóm tài sản --"
                                                                    isClearable={true}
                                                                    className={errors.assetGroupId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.assetGroupId
                                                        },
                                                        {
                                                            label: 'Phòng ban',
                                                            name: 'currentDepartmentId',
                                                            component: (
                                                                <Select
                                                                    options={departmentOptions}
                                                                    value={departmentOptions.find(opt => opt.value === values.currentDepartmentId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('currentDepartmentId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn phòng ban --"
                                                                    isClearable={true}
                                                                    className={errors.currentDepartmentId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.currentDepartmentId
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Số Serial',
                                                            name: 'serialNumber',
                                                            value: values.serialNumber,
                                                            onChange: handleChange,
                                                            isInvalid: errors.serialNumber,
                                                            error: errors.serialNumber
                                                        },
                                                        {
                                                            label: 'Model / Ký hiệu',
                                                            name: 'model',
                                                            value: values.model,
                                                            onChange: handleChange,
                                                            isInvalid: errors.model,
                                                            error: errors.model
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Nhà sản xuất',
                                                            name: 'manufacturer',
                                                            value: values.manufacturer,
                                                            onChange: handleChange,
                                                            isInvalid: errors.manufacturer,
                                                            error: errors.manufacturer
                                                        },
                                                        {
                                                            label: 'Xuất xứ',
                                                            name: 'country',
                                                            value: values.country,
                                                            onChange: handleChange,
                                                            isInvalid: errors.country,
                                                            error: errors.country
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Công suất',
                                                            name: 'powerSpec',
                                                            value: values.powerSpec,
                                                            onChange: handleChange,
                                                            isInvalid: errors.powerSpec,
                                                            error: errors.powerSpec
                                                        },
                                                        {
                                                            label: "Ngày mua",
                                                            name: 'purchaseDate',
                                                            component: (
                                                                <DatePicker
                                                                    wrapperClassName="w-100"
                                                                    className={`form-control ${errors.purchaseDate ? 'is-invalid' : ''}`}
                                                                    selected={values.purchaseDate && moment(values.purchaseDate, 'YYYY-MM-DD').isValid() ? moment(values.purchaseDate, 'YYYY-MM-DD').toDate() : null}
                                                                    onChange={(date) => setFieldValue('purchaseDate', date ? moment(date).format('YYYY-MM-DD') : '')}
                                                                    dateFormat="dd/MM/yyyy"
                                                                    isClearable
                                                                    placeholderText="dd/MM/yyyy"
                                                                />
                                                            ),
                                                            error: errors.purchaseDate
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Giá trị mua (VND)',
                                                            name: 'purchaseValue',
                                                            type: 'number',
                                                            min: 0,
                                                            value: values.purchaseValue,
                                                            onChange: handleChange,
                                                            isInvalid: errors.purchaseValue,
                                                            error: errors.purchaseValue
                                                        },
                                                        {
                                                            label: 'Mã QR Code',
                                                            name: 'qrCode',
                                                            value: values.qrCode,
                                                            onChange: handleChange,
                                                            isInvalid: errors.qrCode,
                                                            error: errors.qrCode,
                                                            disabled: true
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Trạng thái',
                                                            name: 'assetStatusId',
                                                            component: (
                                                                <Select
                                                                    options={assetStatusOptions}
                                                                    value={assetStatusOptions.find(opt => opt.value === values.assetStatusId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('assetStatusId', selectedOption ? selectedOption.value : 0)}
                                                                    placeholder="-- Chọn trạng thái --"
                                                                    isClearable={true}
                                                                    isDisabled={true}
                                                                    // placeholder="-- Trạng thái được cập nhật tự động --"
                                                                    // isClearable={false}
                                                                    // isDisabled={true}
                                                                    className={errors.assetStatusId ? 'is-invalid' : ''}
                                                                />
                                                            ),
                                                            error: errors.assetStatusId
                                                        },
                                                        {
                                                            label: 'Tài sản đã thanh lý',
                                                            name: 'isDisposed',
                                                            component: (
                                                                <Form.Check
                                                                    type="checkbox"
                                                                    label="Đánh dấu tài sản đã được thanh lý"
                                                                    name="isDisposed"
                                                                    checked={values.isDisposed}
                                                                    onChange={handleChange}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />

                                                <h6 className="mb-3 text-primary mt-4">Thông tin bổ sung</h6>
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                            {
                                                                label: 'Mục đích SD',
                                                                name: 'usagePurposeId',
                                                                component: (
                                                                    <Select
                                                                        options={usagePurposeOptions}
                                                                        value={usagePurposeOptions.find(opt => opt.value === values.usagePurposeId) || null}
                                                                        onChange={(selectedOption) => setFieldValue('usagePurposeId', selectedOption ? selectedOption.value : null)}
                                                                        placeholder="-- Chọn mục đích --"
                                                                        isClearable={true}
                                                                    />
                                                                )
                                                            },
                                                        {
                                                            label: 'Người quản lý',
                                                            name: 'manager',
                                                            value: values.manager,
                                                            onChange: handleChange
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Loại trang bị',
                                                            name: 'equipmentTypeId',
                                                            component: (
                                                                <Select
                                                                    options={equipmentTypeOptions}
                                                                    value={equipmentTypeOptions.find(opt => opt.value === values.equipmentTypeId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('equipmentTypeId', selectedOption ? selectedOption.value : null)}
                                                                    placeholder="-- Chọn loại --"
                                                                    isClearable={true}
                                                                />
                                                            )
                                                        },
                                                        {
                                                            label: 'Đơn vị tính',
                                                            name: 'unitOfMeasurement',
                                                            value: values.unitOfMeasurement,
                                                            onChange: handleChange,
                                                            placeholder: 'Cái, Chiếc, Bộ...'
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Nguồn TB',
                                                            name: 'equipmentSourceId',
                                                            component: (
                                                                <Select
                                                                    options={equipmentSourceOptions}
                                                                    value={equipmentSourceOptions.find(opt => opt.value === values.equipmentSourceId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('equipmentSourceId', selectedOption ? selectedOption.value : null)}
                                                                    placeholder="-- Chọn nguồn --"
                                                                    isClearable={true}
                                                                />
                                                            )
                                                        },
                                                        {
                                                            label: 'Nguồn kinh phí',
                                                            name: 'fundingSourceId',
                                                            component: (
                                                                <Select
                                                                    options={fundingSourceOptions}
                                                                    value={fundingSourceOptions.find(opt => opt.value === values.fundingSourceId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('fundingSourceId', selectedOption ? selectedOption.value : null)}
                                                                    placeholder="-- Chọn nguồn --"
                                                                    isClearable={true}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />
                                                <HorizontalFieldsRow
                                                    labelCol={3}
                                                    inputCol={9}
                                                    fields={[
                                                        {
                                                            label: 'Cấp chất lượng',
                                                            name: 'qualityLevelId',
                                                            component: (
                                                                <Select
                                                                    options={qualityLevelOptions}
                                                                    value={qualityLevelOptions.find(opt => opt.value === values.qualityLevelId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('qualityLevelId', selectedOption ? selectedOption.value : null)}
                                                                    placeholder="-- Chọn cấp --"
                                                                    isClearable={true}
                                                                />
                                                            )
                                                        },
                                                        {
                                                            label: 'Loại kết nối',
                                                            name: 'connectionTypeId',
                                                            component: (
                                                                <Select
                                                                    options={connectionTypeOptions}
                                                                    value={connectionTypeOptions.find(opt => opt.value === values.connectionTypeId) || null}
                                                                    onChange={(selectedOption) => setFieldValue('connectionTypeId', selectedOption ? selectedOption.value : null)}
                                                                    placeholder="-- Chọn loại --"
                                                                    isClearable={true}
                                                                />
                                                            )
                                                        }
                                                    ]}
                                                />
                                                <Row>
                                                    <Col md={12}>
                                                        <Form.Group as={Row} className="mb-3">
                                                            <Form.Label className="col-form-label text-sm-right ghichu-label-responsive" style={{ paddingRight: '15px' }}>
                                                                Ghi chú
                                                            </Form.Label>
                                                            <div className="ghichu-input-responsive" style={{ paddingLeft: '5px' }}>
                                                                <Form.Control
                                                                    as="textarea"
                                                                    rows={3}
                                                                    name="notes"
                                                                    value={values.notes}
                                                                    onChange={handleChange}
                                                                    placeholder="Nhập ghi chú bổ sung..."
                                                                />
                                                            </div>
                                                        </Form.Group>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                        <hr />
                                        <div className="d-flex justify-content-end mt-2">
                                            <Button
                                                type="submit"
                                                theme="primary"
                                                disabled={isLoading}
                                                className="mr-2"
                                            >
                                                <i className="fas fa-save mr-1" /> Lưu cập nhật
                                            </Button>
                                            <Button
                                                type="button"
                                                onClick={() => history.goBack()}
                                                theme="secondary"
                                            >
                                                <i className="fas fa-times mr-1" /> Hủy bỏ
                                            </Button>
                                        </div>
                                    </TabPanel>

                                    {/* Tab 2: Hình ảnh & Tài liệu */}
                                    <TabPanel>
                                        <Row className="mt-4">
                                            <Col md={6}>
                                                <Form.Group as={Row} className="mb-3">
                                                    <Form.Label className="col-12 col-sm-3 col-form-label text-left text-sm-right">
                                                        Ảnh tài sản
                                                    </Form.Label>
                                                    <Col sm="9">
                                                        <InputFile
                                                            enableReinitialize={true}
                                                            multiple={false}
                                                            accept={acceptImage}
                                                            labelImage="Tải ảnh lên (Max 2MB)"
                                                            values={(values.imageThumbnail && values.imageThumbnail !== 'null') ? [values.imageThumbnail] : ((values.imagePath && values.imagePath !== 'null') ? [values.imagePath] : [])}
                                                            onChange={(files) => {
                                                                if (files.length > 0) {
                                                                    setFieldValue('imagePath', files[0].filePath || files[0].url, false);
                                                                    setFieldValue('imageThumbnail', files[0].thumbPath || files[0].url, false);
                                                                } else {
                                                                    setFieldValue('imagePath', '', false);
                                                                    setFieldValue('imageThumbnail', '', false);
                                                                }
                                                            }}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                            <Col md={6}>
                                                <Form.Group as={Row} className="mb-3">
                                                    <Form.Label className="col-12 col-sm-3 col-form-label text-left text-sm-right">
                                                        Tài liệu kèm theo
                                                    </Form.Label>
                                                    <Col sm="9">
                                                        <InputFile
                                                            multiple
                                                            isDocument={true}
                                                            name="documentLink"
                                                            accept={acceptDoc}
                                                            values={values.documentLink || []}
                                                            onChange={(files) => {
                                                                setFieldValue(
                                                                    'documentLink',
                                                                    files.map(f => f.filePath || f.url).filter(Boolean),
                                                                    true
                                                                );
                                                            }}
                                                        />
                                                    </Col>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </TabPanel>

                                {/* Các Tab khác nằm trong Form */}
                                {/* Tab 3: Danh sách phụ kiện */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <AccessoryTab assetId={itemId} />
                                    </div>
                                </TabPanel>
                                {/* Tab 4: Lịch sử điều chuyển */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <AccessoryHistoryTab assetId={itemId} />
                                    </div>
                                </TabPanel>

                                {/* Tab 5: Lịch sử sửa chữa */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <MaintenanceEventTab 
                                            assetId={itemId} 
                                            assetStatusId={values.assetStatusId}
                                            assetStatuses={assetStatuses.data?.data || []}
                                        />
                                    </div>
                                </TabPanel>
                                
                                {/* Tab 6: Lịch sử kiểm kê */}
                                <TabPanel>
                                    <div className="mt-3">
                                        <InventoryTab assetId={itemId} />
                                    </div>
                                </TabPanel>
                            </Tabs>
                            </Form>
                        )}
                    </Formik>
                )}
            </Card.Body>
            <style dangerouslySetInnerHTML={{ __html: `
                .react-tabs__tab-list {
                    border-bottom: 1px solid #e9ecef !important;
                    padding-left: 0 !important;
                    margin-bottom: 16px !important;
                    display: flex !important;
                    overflow-x: auto !important;
                    white-space: nowrap !important;
                    scrollbar-width: none !important; /* Hide scrollbar Firefox */
                }
                .react-tabs__tab-list::-webkit-scrollbar {
                    display: none !important; /* Hide scrollbar Chrome/Safari */
                }
                .react-tabs__tab {
                    list-style: none !important;
                    display: inline-block !important;
                    padding: 8px 18px !important;
                    margin-right: 8px !important;
                    margin-bottom: 8px !important;
                    cursor: pointer !important;
                    color: #495057 !important;
                    border: 1px solid #dee2e6 !important;
                    border-radius: 30px !important;
                    background-color: #f8f9fa !important;
                    font-size: 14px !important;
                    font-weight: 500 !important;
                    transition: all 0.2s ease !important;
                    user-select: none !important;
                    bottom: 0 !important;
                    position: relative !important;
                }
                .react-tabs__tab:hover {
                    border-color: #007bff !important;
                    color: #007bff !important;
                    background-color: #e9f5ff !important;
                }
                .react-tabs__tab--selected {
                    background-color: #007bff !important;
                    border-color: #007bff !important;
                    color: #ffffff !important;
                    font-weight: 600 !important;
                    box-shadow: 0 4px 8px rgba(0, 123, 255, 0.15) !important;
                }
                .react-tabs__tab--selected:hover {
                    color: #ffffff !important;
                    background-color: #007bff !important;
                }
                .react-tabs__tab:focus {
                    outline: none !important;
                }
            `}} />
        </Card>
    );
};

export default AssetEdit;
